import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, CalendarDays, Users, RotateCcw, Minus, Plus, Loader2, ShieldAlert } from "lucide-react";
import { BookingStatusPill } from "@/components/BookingStatus";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtTime, fmtDay, TZ } from "@/lib/time";
import { DateTimeField } from "@/components/DateField";
import type { Booking } from "@/types";

const newOp = () => (crypto as any).randomUUID?.() || `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const localNow = () => new Date().toLocaleString("sv-SE", { timeZone: TZ }).slice(0, 16).replace(" ", "T");

const Stat = ({ label, value, id }: { label: string; value: number; id: string }) => (
  <div className="rounded-2xl bg-accent/60 p-3 text-center">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p data-testid={id} className="font-display text-2xl font-black">{value}</p>
  </div>
);

export const AttendancePanel = ({ b, onSaved, onReset }: { b: Booking; onSaved: (b: Booking) => void; onReset: () => void }) => {
  const { t, lang } = usePrefs();
  const arrived = b.attended_count || 0;
  const left = b.guests - arrived;
  const canRecord = ["confirmed", "attended", "no_show"].includes(b.status) && left > 0;
  const [count, setCount] = useState(left);
  const [delayed, setDelayed] = useState(b.status === "no_show");
  const [at, setAt] = useState(b.status === "no_show" ? "" : localNow());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const op = useRef(newOp());

  const save = async () => {
    setBusy(true);
    try {
      const body: any = { op_id: op.current, count };
      if (delayed) Object.assign(body, { arrived_at: new Date(`${at}:00+03:00`).toISOString(), delay_reason: reason });
      const { data } = await api.post(`/venue/bookings/${b.booking_id}/attendance`, body);
      op.current = newOp();
      toast.success(t("b6.recorded").replace("{n}", String(count)).replace("{a}", String(data.attended_count)).replace("{g}", String(data.guests)));
      setCount(Math.max(1, data.guests - (data.attended_count || 0)));
      setReason("");
      onSaved(data);
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const tone = left === 0 ? "border-emerald-500/50 bg-emerald-500/5" : canRecord ? "border-primary/30 bg-card" : "border-rose-500/40 bg-rose-500/5";
  return (
    <div data-testid="checkin-result" className={`rounded-3xl border-2 p-5 space-y-4 ${tone}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p data-testid="checkin-customer-name" className="font-display text-xl font-black">{b.customer_name}</p>
          <p className="text-sm text-muted-foreground">{lang === "ar" ? b.event_title_ar : b.event_title_en}</p>
        </div>
        <BookingStatusPill b={b} />
      </div>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400"><ShieldAlert className="h-4 w-4 shrink-0" />{t("b6.checkName")}</p>
      <div className="flex flex-wrap gap-4 text-sm font-semibold">
        <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" />{fmtDay(b.date, lang)}</span>
        <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" />{b.guests} {t("bookings.guests")}</span>
        {b.code && <span className="font-mono" dir="ltr">{b.code}</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label={t("b6.booked")} value={b.guests} id="checkin-booked" />
        <Stat label={t("b6.arrived")} value={arrived} id="checkin-arrived" />
        <Stat label={t("b6.remaining")} value={left} id="checkin-remaining" />
      </div>
      {left === 0 && <p data-testid="checkin-done-message" className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold text-sm"><CheckCircle2 className="h-5 w-5" />{t("b6.allArrived")}</p>}
      {!canRecord && left > 0 && (
        <p className="flex items-center gap-2 text-rose-600 font-bold text-sm"><AlertTriangle className="h-5 w-5" />{b.status === "cancelled" ? t("checkin.cancelledNote") : t("b5.notConfirmed")}</p>
      )}
      {canRecord && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-bold">{t("b6.arrivingNow")}</span>
            <div className="flex items-center gap-3">
              <button data-testid="attendance-minus" onClick={() => setCount(Math.max(1, count - 1))} className="h-10 w-10 rounded-full border grid place-items-center"><Minus className="h-4 w-4" /></button>
              <span data-testid="attendance-count" className="w-8 text-center text-2xl font-black">{count}</span>
              <button data-testid="attendance-plus" onClick={() => setCount(Math.min(left, count + 1))} className="h-10 w-10 rounded-full border grid place-items-center"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={delayed} disabled={b.status === "no_show"} onChange={(e) => setDelayed(e.target.checked)} data-testid="attendance-delayed-toggle" className="h-4 w-4 accent-[hsl(var(--primary))]" />
            {t("b6.delayed")}
          </label>
          {delayed && (
            <div className="space-y-2 rounded-2xl border border-dashed p-3">
              <p className="text-xs text-muted-foreground">{t("b6.delayedNote")}</p>
              <label className="text-xs font-bold">{t("b6.arrivedAt")}</label>
              <DateTimeField value={at} max={localNow()} onChange={setAt} testId="attendance-arrived-at" />
              <label className="text-xs font-bold">{t("b6.delayReason")}</label>
              <Input value={reason} onChange={(e: any) => setReason(e.target.value)} maxLength={300} data-testid="attendance-delay-reason" className="h-11 rounded-2xl" />
            </div>
          )}
          <button data-testid="checkin-confirm-button" disabled={busy || (delayed && (reason.trim().length < 3 || !at))} onClick={save}
            className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-60 active:scale-[0.98] transition-[transform,background-color] inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{t("b6.saving")}</> : t("b6.record").replace("{n}", String(count))}
          </button>
        </div>
      )}
      {!!b.attendance?.length && (
        <div data-testid="attendance-history" className="space-y-1.5">
          <p className="text-xs font-bold text-muted-foreground">{t("b6.history")}</p>
          {b.attendance.map((o) => (
            <p key={o.op_id} className="text-xs flex flex-wrap gap-x-2">
              <b>+{o.count}</b><span>{fmtTime(o.arrived_at, lang)}</span><span className="text-muted-foreground">{o.by}</span>
              {o.delayed && <span className="text-amber-700 dark:text-amber-400 font-semibold">{t("b6.late")}: {o.reason}</span>}
            </p>
          ))}
        </div>
      )}
      <button data-testid="checkin-reset-button" onClick={onReset} className="h-11 px-5 rounded-full border font-bold text-sm inline-flex items-center gap-1.5 hover:bg-accent">
        <RotateCcw className="h-4 w-4" />{t("checkin.scanAgain")}
      </button>
    </div>
  );
};
